import type { Usage } from '@botpress/client'
import { QuotaType } from '@bpinternal/const'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@bpinternal/ui-kit-next'
import { ComponentProps, ReactNode, forwardRef } from 'react'
import { Label, Pie, PieChart } from 'recharts'
import FileEmptyStateIcon from '~/assets/data-analytics-01.svg?react'
import VectorEmptyStateIcon from '~/assets/data-analytics-04.svg?react'
import TableEmptyStateIcon from '~/assets/data-analytics-14.svg?react'
import AiSpendEmptyStateIcon from '~/assets/data-analytics-15.svg?react'
import InvocationEmptyStateIcon from '~/assets/programming-04.svg?react'
import { EmptyState } from '~/elements'
import { QUOTA_TYPE_USER_FACING_PROPERTIES_MAP } from '~/features/usage/constants'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { cn } from '~/utils'
import { segregateUsages } from '../utils'
import { BreakdownLegend } from './BreakdownLegend'

type DetailedUsage = Partial<Usage> & {
  breakdown: {
    id: string
    value: number
    name: string
  }[]
}
const valueFormatter = (type: QuotaType) => (value: number) => formatValueWithUnit(value, type, 0)
const topN = 3

const getUsageEmptyStateIcon = (type: QuotaType) => {
  switch (type) {
    case 'ai_spend':
      return AiSpendEmptyStateIcon
    case 'storage_count':
      return FileEmptyStateIcon
    case 'table_row_count':
      return TableEmptyStateIcon
    case 'knowledgebase_vector_storage':
      return VectorEmptyStateIcon
    case 'invocation_calls':
      return InvocationEmptyStateIcon
    default:
      return InvocationEmptyStateIcon
  }
}

const sanitizeId = (text: string) => {
  return text.replaceAll(/[^a-zA-Z0-9_-]/g, '_')
}

type UsageCardProps = {
  usage: DetailedUsage
  type: QuotaType
  size?: 'sm' | 'lg'
  description?: ReactNode
  title?: string
  contentClassName?: string
} & ComponentProps<typeof Card>
export const BreakdownCard = forwardRef<HTMLDivElement, UsageCardProps>(
  ({ usage: unsanitizedUsage, type, size = 'sm', className, description, title, contentClassName, ...props }, ref) => {
    const usage: DetailedUsage = {
      ...unsanitizedUsage,
      breakdown: unsanitizedUsage.breakdown.map((item) => ({
        ...item,
        id: sanitizeId(item.id),
      })),
      id: sanitizeId(unsanitizedUsage.id ?? ''),
    }

    const { usages, remainder } = segregateUsages(usage.breakdown, topN)
    const total = usages.reduce((acc, { value }) => acc + value, 0) + (remainder ?? 0)
    const chartData = usages.map((usage) => ({
      ...usage,
      fill: `var(--color-${usage.id})`,
    }))

    if (remainder !== undefined) {
      chartData.push({
        value: remainder,
        name: 'Other',
        id: 'other',
        fill: 'var(--color-other)',
      })
    }

    if (usage.quota && usage.value && usage.quota > usage.value) {
      chartData.push({
        value: usage.quota - usage.value,
        name: 'Remaining',
        id: 'remaining',
        fill: 'var(--color-remaining)',
      })
    }

    const chartConfig = chartData.reduce<ChartConfig>((acc, { id, name }, i) => {
      return {
        ...acc,
        [id]: { label: name, color: id === 'remaining' ? 'hsl(var(--secondary))' : `hsl(var(--chart-${i + 1}))` }, //Very ugly but will do the trick for now
      }
    }, {})

    return (
      <Card {...props} ref={ref} className={cn('flex flex-col @container', className)}>
        <CardHeader>
          <CardTitle>{title ? title : QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}</CardTitle>
          <CardDescription>
            {description ? description : QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].description}
          </CardDescription>
        </CardHeader>
        <CardContent className={cn('mt-auto', { 'mb-auto': usage.breakdown.length === 0 })}>
          {usage.breakdown.length === 0 ? (
            <EmptyState
              className="grow gap-2 self-center"
              icon={getUsageEmptyStateIcon(usage.type ?? 'ai_spend')}
              description="No usage data available for this period."
              descriptionSize="1"
            />
          ) : (
            <div className={cn('flex grow flex-col items-center gap-4 @xs:flex-row', contentClassName)}>
              <ChartContainer
                config={chartConfig}
                className={cn('mx-auto aspect-square max-h-[250px]', size === 'lg' ? 'size-52' : 'size-36')}
              >
                <PieChart>
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent hideLabel valueFormatter={valueFormatter(type) as any} />}
                  />
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="id"
                    innerRadius={size === 'lg' ? 60 : 42}
                    strokeWidth={5}
                  >
                    <Label
                      content={({ viewBox }) => {
                        if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                          return (
                            <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                              <tspan
                                x={viewBox.cx}
                                y={viewBox.cy}
                                className={cn('fill-foreground font-bold', {
                                  'text-xl': size === 'lg',
                                  'text-base': size === 'sm',
                                })}
                              >
                                {`${formatValueWithUnit(usage.value ?? total, type)}`}
                              </tspan>
                              {usage.quota && (
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 24}
                                  className={cn('fill-muted-foreground')}
                                >
                                  {`of ${formatValueWithUnit(usage.quota, type)}`}
                                </tspan>
                              )}
                            </text>
                          )
                        }
                      }}
                    />
                  </Pie>
                </PieChart>
              </ChartContainer>
              <BreakdownLegend
                topN={topN}
                breakdowns={usage.breakdown}
                valueFormatter={(value: number) => formatValueWithUnit(value, type)}
              />
            </div>
          )}
        </CardContent>
      </Card>
    )
  }
)
BreakdownCard.displayName = 'BreadownCard'
